import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BookingHeader from '../components/BookingHeader';
import BookingProgressFooter from '../components/BookingProgressFooter';
import { EventBanner } from '../components/EventBanner';
import { BookingChrome } from './BookingChrome';
import { useBookingFlow } from '../flow';
import type { BookingStepId } from '../flow';
import { useAgentDetection } from '../hooks/useAgentDetection';

/**
 * Map step IDs to translation keys for screen reader announcements.
 */
const STEP_ANNOUNCEMENT_KEYS: Record<BookingStepId, string> = {
  DINERS: 'AMOUNT_OF',
  DATE: 'STRING_SELECT',
  SERVICE: 'SERVICE_TITLE',
  EARLY_ACCESS: 'EARLY_ACCESS_BANNER_TITLE',
  MENU_FILTER: 'MARIDAJE_TITLE',
  AREA_FILTER: 'AREA_FILTER_TITLE',
  EXPERIENCE: 'STRING_PAYMENT_STEP_1',
  AREA: 'AREA_TITLE',
  TIME: 'TIME_TITLE',
  MARIDAJE: 'MARIDAJE_TITLE',
  PERSONALIZE: 'NEW_MODAL_PERSONALIZE_EXPERIENCE',
  AGENT_GUEST_INFO: 'AGENT_TITLE_1',
  GUARANTEE: 'STRING_ENTER_PAYMENT_DATA',
  SIGN_IN: 'COMPLETE_PROFILE_TITLE',
  COMPLETE_PROFILE: 'COMPLETE_PROFILE_TITLE',
  WL_FORM: 'WAITING_LIST_FORM_SUBTITLE',
  WL_CALENDAR: 'WAITING_LIST_CALENDAR_TITLE',
  WL_ALTERNATIVES: 'WAITING_LIST_CONFIRM_TITLE',
  CONFIRMATION: 'CONFIRMATION_TITLE',
  BOOKING_FINAL: 'GRAB_CONFIRMATION_TITLE',
};

interface BookingPageLayoutProps {
  title?: string;
  subtitle?: string;
  breadcrumbs?: ReactNode;
  children: ReactNode;
  onBack?: () => void;
  progress?: number;
  showBack?: boolean;
  contentClassName?: string;
  subtitleClassName?: string;
}

export function BookingPageLayout({
  title,
  subtitle,
  breadcrumbs,
  children,
  onBack,
  progress: progressOverride,
  showBack = true,
  contentClassName = 'pt-8 pb-10 flex flex-col items-center',
  subtitleClassName = 'text-foreground/50 text-sm mb-10',
}: BookingPageLayoutProps) {
  useAgentDetection();
  const { t } = useTranslation('booking');
  const { goToPrevious, progress: flowProgress, currentStep } = useBookingFlow();

  const handleBack = onBack ?? goToPrevious;
  const progress = progressOverride ?? flowProgress;

  // Track step changes for aria-live announcement
  const [stepAnnouncement, setStepAnnouncement] = useState('');
  const prevStepRef = useRef<string | null>(null);

  useEffect(() => {
    const stepId = currentStep?.id ?? null;
    if (stepId && stepId !== prevStepRef.current) {
      const key = STEP_ANNOUNCEMENT_KEYS[stepId];
      if (key) {
        // Clear before rAF so same-step re-navigation triggers a fresh announcement
        setStepAnnouncement('');
        const handle = requestAnimationFrame(() => setStepAnnouncement(t(key)));
        prevStepRef.current = stepId;
        return () => cancelAnimationFrame(handle);
      }
    }
    prevStepRef.current = stepId;
  }, [currentStep?.id, t]);

  return (
    <BookingChrome>
      {/* Screen reader step transition announcement. Visually clipped via
          sr-only; aria-live polite + atomic makes the new value announce
          when stepAnnouncement updates. */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {stepAnnouncement}
      </div>
      <BookingHeader />

      {breadcrumbs && (
        <div className="w-full px-3 sm:px-6 pt-6 pb-3 flex justify-center">
          {breadcrumbs}
        </div>
      )}

      <EventBanner />

      <div className={`booking-content ${contentClassName}`}>
        {title && <h1 className="text-3xl sm:text-4xl font-display mb-3 text-center">{title}</h1>}
        {subtitle && <p className={subtitleClassName}>{subtitle}</p>}
        {children}
      </div>

      <BookingProgressFooter onBack={handleBack} progress={progress} showBack={showBack} />
    </BookingChrome>
  );
}

export default BookingPageLayout;
